import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { GradeEntry, Assignment, Student } from '../types';

interface GradeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  grade?: GradeEntry;
  assignment: Assignment;
  student: Student;
  onUpdateFeedback: (feedback: string) => void;
}

const GradeDetailsModal = ({
  isOpen,
  onClose,
  grade,
  assignment,
  student,
  onUpdateFeedback,
}: GradeDetailsModalProps) => {
  const [feedback, setFeedback] = React.useState(grade?.feedback || '');

  if (!isOpen) return null;

  const getAssignmentTypeLabel = (type: Assignment['type']) => {
    switch (type) {
      case 'homework':
        return 'Temă';
      case 'quiz':
        return 'Test';
      case 'exam':
        return 'Examen';
      case 'project':
        return 'Proiect';
      case 'participation':
        return 'Participare';
      default:
        return type;
    }
  };

  const handleSaveFeedback = () => {
    onUpdateFeedback(feedback);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[1100] animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[1110] flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Detalii notă</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {student.name} - {assignment.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
              aria-label="Închide fereastra"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Elev</span>
                </div>
                <p className="text-sm text-foreground">{student.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{student.studentId}</p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="FileText" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Evaluare</span>
                </div>
                <p className="text-sm text-foreground">{assignment.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{getAssignmentTypeLabel(assignment.type)}</p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Award" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Notă</span>
                </div>
                <p className="text-lg font-semibold text-foreground">
                  {grade
                    ? grade.qualification || `${grade.score}/${assignment.maxScore}`
                    : 'Neevaluat'}
                </p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Predat</span>
                </div>
                <p className="text-sm text-foreground">
                  {grade?.submittedDate
                    ? new Date(grade.submittedDate).toLocaleDateString('ro-RO')
                    : 'Nepredat'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Descriere evaluare
              </label>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-foreground">{assignment.description}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Feedback profesor
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Adaugă feedback pentru elev..."
                rows={4}
                className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-150"
              />
            </div>

            {grade && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Ultima modificare</span>
                </div>
                <p className="text-sm text-foreground">
                  {new Date(grade.lastModified).toLocaleString('ro-RO')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">De către {grade.modifiedBy}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button variant="default" onClick={handleSaveFeedback} iconName="Save">
              Salvează feedback
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GradeDetailsModal;
